import { isDefined } from "@clipboard-health/util-ts";
import { Box, Button, Stack } from "@mui/material";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { useGetMultiLicenseAggregatedBlocks } from "@src/appV2/redesign/ShiftDiscovery/Blocks/useGetMultiLicenseAggregatedBlocks";
import { type Worker } from "@src/appV2/Worker/api/types";

import { useFetchPaginatedShiftBlocks } from "../api/useFetchPaginatedShiftBlocks";
import { useAllRolesFilterContext } from "../hooks/useAllRolesFilter";
import { useEnableDistanceFiltering } from "../hooks/useEnableDistanceFiltering";
import { transformIntoShiftBlockWithDetails } from "../utils";
import { EmptyShiftBlock } from "./EmptyShiftBlock";
import { EmptyShiftBlocksFacilityView } from "./EmptyShiftBlocksFacilityView";
import { ShiftBlockCardSkeleton } from "./ShiftBlockCardSkeleton";
import { ShiftBlockItem } from "./ShiftBlockItem";

interface ShiftBlocksProps {
  worker: Worker;
  facilityId?: string;
  orientationBlockQualification?: string;
}

function ShiftBlocksEmptyState(props: { facilityId?: string }) {
  const { facilityId } = props;

  if (isDefined(facilityId)) {
    return (
      <Box sx={{ paddingY: 1.5, paddingX: 1 }}>
        <EmptyShiftBlocksFacilityView />
      </Box>
    );
  }

  return <EmptyShiftBlock />;
}

function getNormalizedPages(
  showAllQualifications: boolean,
  allQualificationsData: ReturnType<typeof useGetMultiLicenseAggregatedBlocks>["data"],
  filteredPages: ReturnType<typeof useFetchPaginatedShiftBlocks>["data"]
) {
  if (showAllQualifications) {
    return allQualificationsData ? [allQualificationsData] : [];
  }
  return filteredPages?.pages ?? [];
}

export function ShiftBlocks(props: ShiftBlocksProps) {
  const { worker, facilityId, orientationBlockQualification } = props;

  const isOrientationBlock = isDefined(orientationBlockQualification);

  const { filters } = useWorkerPreferences();
  const { isAllRolesSelected } = useAllRolesFilterContext();
  const { enableDistanceFiltering } = useEnableDistanceFiltering();

  const filterQualification = filters?.license ?? worker?.licensesData[0]?.qualification;
  const showAllQualifications = isAllRolesSelected;
  const qualification = isOrientationBlock ? orientationBlockQualification : filterQualification;
  const distanceMiles = enableDistanceFiltering ? filters.distance : undefined;

  const allQualificationsQuery = useGetMultiLicenseAggregatedBlocks(
    { maxDistanceMiles: distanceMiles },
    { enabled: showAllQualifications && !isOrientationBlock && !isDefined(facilityId) }
  );

  const filteredQuery = useFetchPaginatedShiftBlocks(
    {
      filter: {
        agentId: worker.userId,
        facilityId,
        qualification: qualification ?? "",
        maxDistanceMiles: distanceMiles?.toString(),
        ...(isOrientationBlock && { isOrientationBlock }),
      },
    },
    {
      enabled: !showAllQualifications && isDefined(qualification),
      meta: { userErrorMessage: "Something went wrong while loading shift blocks" },
    }
  );

  const isLoading = showAllQualifications
    ? allQualificationsQuery.isLoading
    : filteredQuery.isLoading;
  const isSuccess = showAllQualifications
    ? allQualificationsQuery.isSuccess
    : filteredQuery.isSuccess;

  const normalizedPages = getNormalizedPages(
    showAllQualifications,
    allQualificationsQuery.data,
    filteredQuery.data
  );

  const hasData = normalizedPages.length > 0 && (normalizedPages[0]?.data.length ?? 0) > 0;

  if (isSuccess && !hasData) {
    return <ShiftBlocksEmptyState facilityId={facilityId} />;
  }

  if (!showAllQualifications && !isDefined(qualification)) {
    return <EmptyShiftBlock />;
  }

  return (
    <Stack spacing={2}>
      {isLoading && <ShiftBlockCardSkeleton />}
      {normalizedPages.map((shiftBlockPage) =>
        shiftBlockPage.data.map((shiftBlock) => {
          const shiftBlockWithDetails = transformIntoShiftBlockWithDetails(
            shiftBlock,
            shiftBlockPage.included
          );
          return (
            <ShiftBlockItem
              key={shiftBlock.id}
              shiftBlock={shiftBlockWithDetails}
              agentTimezone={worker.tmz}
              shouldShowFacilityDetails={!isDefined(facilityId)}
              showStartEndTime={!isOrientationBlock}
            />
          );
        })
      )}
      {!showAllQualifications && filteredQuery.hasNextPage && (
        <Button
          fullWidth
          size="small"
          disabled={filteredQuery.isFetchingNextPage}
          onClick={async () => await filteredQuery.fetchNextPage()}
        >
          Load more blocks
        </Button>
      )}
    </Stack>
  );
}
